// apps/accounts/src/pages/materials/PurchaseOrderDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  XCircle,
  Package,
  Truck,
  Calendar,
  DollarSign,
  User,
  Building2,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const statusConfig = {
  Draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  Sent: { label: 'Sent to Supplier', color: 'bg-blue-100 text-blue-700' },
  'Partial Received': { label: 'Partial Received', color: 'bg-amber-100 text-amber-700' },
  Received: { label: 'Received', color: 'bg-green-100 text-green-700' },
  Cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const PurchaseOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const canReceive = ['admin', 'store_keeper'].includes(role);
  const canEdit = ['admin', 'accounts'].includes(role);
  const canDelete = ['admin'].includes(role);

  const [po, setPo] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [receiveData, setReceiveData] = useState({});
  const [receiveLoading, setReceiveLoading] = useState(false);

  const fetchData = async () => {
    try {
      // ✅ Fetch PO with supplier instead of vendor
      const poData = await pb.collection('material_purchase_orders').getOne(id, {
        expand: 'supplier,project,created_by', // changed from vendor to supplier
        requestKey: null,
      });
      setPo(poData);

      // Fetch PO items
      const itemsData = await pb.collection('material_purchase_order_items').getFullList({
        filter: `purchase_order = "${id}"`,
        expand: 'material,uom',
        requestKey: null,
      });
      setItems(itemsData);

      // Initialize receive quantities with remaining
      const initReceive = {};
      itemsData.forEach((item) => {
        const remaining = item.quantity - (item.received_quantity || 0);
        initReceive[item.id] = remaining > 0 ? remaining : 0;
      });
      setReceiveData(initReceive);
    } catch (err) {
      if (err.isAbort) return;
      console.error('Error fetching PO:', err);
      toast.error('Failed to load purchase order');
      navigate('/materials/purchase-orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this purchase order?')) return;
    try {
      // Delete items first
      for (const item of items) {
        await pb.collection('material_purchase_order_items').delete(item.id);
      }
      await pb.collection('material_purchase_orders').delete(id);
      toast.success('Purchase order deleted');
      navigate('/materials/purchase-orders');
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Delete failed: ' + err.message);
    }
  };

  const handleReceive = async () => {
    // Check if any quantity entered
    const totalReceiving = Object.values(receiveData).reduce((sum, qty) => sum + Number(qty || 0), 0);
    if (totalReceiving === 0) {
      toast.error('Please enter at least one quantity to receive');
      return;
    }

    setReceiveLoading(true);
    try {
      // Process each item
      for (const item of items) {
        const qty = Number(receiveData[item.id] || 0);
        if (qty <= 0) continue;

        // Create transaction
        await pb.collection('material_transactions').create({
          material: item.material,
          type: 'purchase',
          quantity: qty,
          unit_cost: item.unit_price,
          total_cost: qty * item.unit_price,
          date: new Date().toISOString().split('T')[0],
          reference: `PO-${po.po_number || id.slice(0, 8)}`,
          note: `Received from PO ${po.po_number || id.slice(0, 8)}`,
          user: user.id,
        });

        // Update material stock & avg_cost
        const mat = await pb.collection('materials').getOne(item.material);
        const newStock = (mat.current_stock || 0) + qty;
        // Update avg cost (weighted average)
        const newAvgCost = mat.avg_cost
          ? ((mat.avg_cost * mat.current_stock) + (qty * item.unit_price)) / (mat.current_stock + qty)
          : item.unit_price;
        const status = newStock <= 0 ? 'Out of Stock' : (newStock < mat.min_stock ? 'Low Stock' : 'Available');
        await pb.collection('materials').update(item.material, {
          current_stock: newStock,
          avg_cost: newAvgCost,
          status,
        });

        // Update item received_quantity
        const receivedSoFar = (item.received_quantity || 0) + qty;
        await pb.collection('material_purchase_order_items').update(item.id, {
          received_quantity: receivedSoFar,
        });
      }

      // Update PO status
      const allReceived = items.every((item) => {
        const received = item.received_quantity || 0;
        return received >= item.quantity;
      });
      const newStatus = allReceived ? 'Received' : 'Partial Received';
      await pb.collection('material_purchase_orders').update(id, { status: newStatus });

      toast.success(`Items received. Status: ${newStatus}`);
      fetchData();
      setReceiving(false);
    } catch (err) {
      console.error('Receive error:', err);
      toast.error('Receive failed: ' + err.message);
    } finally {
      setReceiveLoading(false);
    }
  };

  const handleReceiveQtyChange = (itemId, value) => {
    const item = items.find((i) => i.id === itemId);
    const maxQty = item.quantity - (item.received_quantity || 0);
    const newVal = Math.min(Math.max(0, Number(value) || 0), maxQty);
    setReceiveData((prev) => ({ ...prev, [itemId]: newVal }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" />
      </div>
    );
  }

  if (!po) return <div className="p-6 text-center text-slate-500">Purchase order not found</div>;

  const statusInfo = statusConfig[po.status] || statusConfig.Draft;
  const totalAmount = items.reduce((sum, item) => sum + (item.total || 0), 0);

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/materials/purchase-orders" className="text-sm text-amber-600 hover:underline flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Purchase Orders
          </Link>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 mt-1">
            <Package className="w-6 h-6 text-amber-500" />
            PO #{po.po_number || po.id.slice(0, 8)}
          </h1>
          <p className="text-sm text-slate-500 flex items-center gap-2">
            Status: <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          {canEdit && po.status === 'Draft' && (
            <Button
              variant="outline"
              className="text-amber-600 border-amber-200 hover:bg-amber-50"
              onClick={() => navigate(`/materials/purchase-orders/edit/${id}`)}
            >
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
          )}
          {canDelete && po.status === 'Draft' && (
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={handleDelete}
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          )}
          {canReceive && (po.status === 'Sent' || po.status === 'Partial Received') && (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => setReceiving(!receiving)}
            >
              <Truck className="w-4 h-4 mr-1" /> {receiving ? 'Cancel Receiving' : 'Receive Items'}
            </Button>
          )}
        </div>
      </div>

      {/* ─── PO Info ────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-slate-500 font-medium">Supplier</p>
            <p className="font-semibold flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              {po.expand?.supplier?.companyName || po.expand?.supplier?.name || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Project</p>
            <p className="font-semibold">{po.expand?.project?.name || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Total Amount</p>
            <p className="font-bold text-lg text-indigo-600">{formatCurrency(totalAmount)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Order Date</p>
            <p className="font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              {formatDate(po.order_date)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Expected Delivery</p>
            <p className="font-semibold">{po.expected_delivery ? formatDate(po.expected_delivery) : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Created By</p>
            <p className="font-semibold flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              {po.expand?.created_by?.name || 'System'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ─── Items Table ────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Material</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">UOM</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600">Qty Ordered</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600">Received</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600">Unit Price</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600">Total</th>
                {receiving && canReceive && (
                  <th className="px-4 py-3 text-center font-semibold text-slate-600">Receive Qty</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item) => {
                const remaining = item.quantity - (item.received_quantity || 0);
                return (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition">
                    <td className="px-4 py-3 font-medium">{item.expand?.material?.name || 'N/A'}</td>
                    <td className="px-4 py-3">{item.expand?.uom?.symbol || '—'}</td>
                    <td className="px-4 py-3 text-right font-mono">{item.quantity}</td>
                    <td className="px-4 py-3 text-right font-mono text-green-600">{item.received_quantity || 0}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatCurrency(item.unit_price)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{formatCurrency(item.total)}</td>
                    {receiving && canReceive && (
                      <td className="px-4 py-3 text-center">
                        <Input
                          type="number"
                          value={receiveData[item.id] || 0}
                          onChange={(e) => handleReceiveQtyChange(item.id, e.target.value)}
                          className="w-20 text-center inline-block"
                          min="0"
                          max={remaining}
                          step="0.01"
                          disabled={receiveLoading}
                        />
                        <span className="text-xs text-slate-400 ml-1">/ {remaining}</span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr>
                <td colSpan={receiving && canReceive ? 5 : 4} className="px-4 py-3 text-right font-bold">
                  Grand Total
                </td>
                <td className="px-4 py-3 text-right font-bold text-indigo-600">
                  {formatCurrency(totalAmount)}
                </td>
                {receiving && canReceive && <td></td>}
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {/* ─── Note ────────────────────────────────────────────────── */}
      {po.note && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
          <p className="text-sm text-slate-600">
            <span className="font-medium">Note:</span> {po.note}
          </p>
        </div>
      )}

      {/* ─── Receive Action ────────────────────────────────────── */}
      {receiving && canReceive && (po.status === 'Sent' || po.status === 'Partial Received') && (
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setReceiving(false)}>
            Cancel
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={handleReceive}
            disabled={receiveLoading}
          >
            {receiveLoading ? 'Processing...' : 'Confirm Receiving'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderDetail;